/**
 * Route Edit Service
 * Pure functions for segment recalculation when route points are moved or deleted.
 */
import { generateId } from '@/services/idService'
import { routingService } from '@/services/routingService'
import type { RouteResult } from '@/services/routingService'
import type { Segment, Waypoint } from '@/types/tour'
import { ConcurrencyLimiter } from '@/utils/concurrencyLimiter'

function routeResultToSegment(result: RouteResult, time?: number): Segment {
  const points: Waypoint[] = result.points.map((p) => ({
    clientId: generateId(),
    lat: p.lat,
    lon: p.lon,
    ele: p.ele,
    time,
    time_offset: p.time_offset,
    distance: p.distance,
    cost_per_km: p.cost_per_km,
    elev_cost: p.elev_cost,
    turn_cost: p.turn_cost,
    node_cost: p.node_cost,
    initial_cost: p.initial_cost,
    way_tags: p.way_tags,
    node_tags: p.node_tags,
  }))
  return {
    clientId: generateId(),
    points,
    creator: result.creator,
    profile: result.profile,
    trackLength: result.trackLength,
    totalTime: result.totalTime,
    filteredAscend: result.filteredAscend,
    plainAscend: result.plainAscend,
    filteredDescend: result.filteredDescend,
    plainDescend: result.plainDescend,
    totalEnergy: result.totalEnergy,
    cost: result.cost,
    voiceHints: result.voiceHints?.map((h) => ({
      nodeIndex: h.nodeIndex,
      command: h.command,
      exitNumber: h.exitNumber,
      distance: h.distance,
      angle: h.angle,
    })),
  }
}

/**
 * Convert a BRouter RouteResult into a Segment, stamping each waypoint's time
 * with `timestamp`. Exposed for consumers (drawing/edit/drag composables) that
 * need to preserve BRouter per-node and per-segment data when building segments.
 */
export function routeResultToSegmentWithTime(result: RouteResult, timestamp: number): Segment {
  return routeResultToSegment(result, timestamp)
}

/**
 * Route between two points and return a Segment.
 */
export async function routeSegment(
  from: { lat: number; lon: number },
  to: { lat: number; lon: number },
  profile: string,
): Promise<Segment | null> {
  const result = await routingService.getRoute(
    [
      [from.lat, from.lon],
      [to.lat, to.lon],
    ],
    profile,
  )
  if (!result) return null
  return routeResultToSegment(result)
}

/**
 * Recalculate segments after inserting a new route point between two existing ones.
 * Splits segment[insertBeforeIndex - 1] (which connects rp[insertBeforeIndex - 1] → rp[insertBeforeIndex])
 * into two new segments: rp[insertBeforeIndex - 1] → newPos and newPos → rp[insertBeforeIndex].
 *
 * @param routePoints current route points (the new point is NOT yet inserted)
 * @param insertBeforeIndex the array index at which the new route point will be inserted (1..N-1)
 */
export async function recalcSegmentsForInsert(
  routePoints: { lat: number; lon: number }[],
  insertBeforeIndex: number,
  newLat: number,
  newLon: number,
  profile: string,
): Promise<{
  deletedIndices: number[]
  newSegments: { segment: Segment; order: number }[]
} | null> {
  if (insertBeforeIndex <= 0 || insertBeforeIndex >= routePoints.length) return null

  const prev = routePoints[insertBeforeIndex - 1]
  const next = routePoints[insertBeforeIndex]
  const newPos = { lat: newLat, lon: newLon }

  const [segA, segB] = await Promise.all([
    routeSegment(prev, newPos, profile),
    routeSegment(newPos, next, profile),
  ])

  if (!segA || !segB) return null

  const deletedSegmentIndex = insertBeforeIndex - 1
  return {
    deletedIndices: [deletedSegmentIndex],
    newSegments: [
      { segment: segA, order: deletedSegmentIndex },
      { segment: segB, order: deletedSegmentIndex + 1 },
    ],
  }
}

/**
 * Recalculate segments after moving a route point.
 * Only recalcs the 2 adjacent segments (before and after the moved point) in parallel.
 * Returns null if routing fails.
 */
export async function recalcSegmentsForMove(
  segments: Segment[],
  routePoints: { lat: number; lon: number }[],
  rpIndex: number,
  newLat: number,
  newLon: number,
  profile: string,
): Promise<{
  deletedIndices: number[]
  newSegments: { segment: Segment; order: number }[]
} | null> {
  const total = routePoints.length
  const newPos = { lat: newLat, lon: newLon }

  // Determine which segments are affected
  const deletedIndices: number[] = []
  const routePromises: Promise<Segment | null>[] = []
  const orders: number[] = []

  // Segment before: connects rp[rpIndex-1] → rp[rpIndex]
  if (rpIndex > 0) {
    deletedIndices.push(rpIndex - 1)
    routePromises.push(routeSegment(routePoints[rpIndex - 1], newPos, profile))
    orders.push(rpIndex - 1)
  }

  // Segment after: connects rp[rpIndex] → rp[rpIndex+1]
  if (rpIndex < total - 1) {
    deletedIndices.push(rpIndex)
    routePromises.push(routeSegment(newPos, routePoints[rpIndex + 1], profile))
    orders.push(rpIndex)
  }

  const results = await Promise.all(routePromises)

  // Check if any routing failed
  if (results.some((r) => r === null)) return null

  const newSegments = results.map((seg, i) => ({
    segment: seg as Segment,
    order: orders[i],
  }))

  return { deletedIndices, newSegments }
}

/**
 * Recalculate segments after deleting a route point.
 * Routes the gap between the neighboring points, replacing 1 or 2 old segments.
 * Returns null if routing fails.
 */
export async function recalcSegmentsForDelete(
  segments: Segment[],
  routePoints: { lat: number; lon: number }[],
  rpIndex: number,
  profile: string,
): Promise<{
  deletedIndices: number[]
  newSegments: { segment: Segment; order: number }[]
} | null> {
  const total = routePoints.length
  const deletedIndices: number[] = []

  // Segment before the deleted point
  if (rpIndex > 0) deletedIndices.push(rpIndex - 1)
  // Segment after the deleted point
  if (rpIndex < total - 1) deletedIndices.push(rpIndex)

  // If deleting first or last point with only the edge segment, just delete it
  if (rpIndex === 0 && total <= 2) {
    return { deletedIndices, newSegments: [] }
  }
  if (rpIndex === total - 1 && total <= 2) {
    return { deletedIndices, newSegments: [] }
  }

  // Route between the neighbors of the deleted point
  const prevPoint = rpIndex > 0 ? routePoints[rpIndex - 1] : null
  const nextPoint = rpIndex < total - 1 ? routePoints[rpIndex + 1] : null

  if (prevPoint && nextPoint) {
    // Middle point: merge two segments into one
    const merged = await routeSegment(prevPoint, nextPoint, profile)
    if (!merged) return null
    // Use the lower order index for the merged segment
    const order = Math.min(...deletedIndices)
    return { deletedIndices, newSegments: [{ segment: merged, order }] }
  }

  // Edge point (first or last) with more than 2 points: just remove the edge segment
  return { deletedIndices, newSegments: [] }
}

/**
 * Rebuild all segments from route points (for API tracks that lack local segment data).
 * Routes between consecutive route points sequentially.
 */
export async function rebuildAllSegments(
  routePoints: { lat: number; lon: number }[],
  profile: string,
  options?: { maxConcurrency?: number },
): Promise<Segment[] | null> {
  if (routePoints.length < 2) return []

  const maxConcurrency = Math.max(1, options?.maxConcurrency ?? 1)
  if (maxConcurrency === 1) {
    const segments: Segment[] = []
    for (let i = 0; i < routePoints.length - 1; i++) {
      const seg = await routeSegment(routePoints[i], routePoints[i + 1], profile)
      if (!seg) return null
      segments.push(seg)
    }
    return segments
  }

  const limiter = new ConcurrencyLimiter(maxConcurrency)
  const segmentPromises = routePoints
    .slice(0, -1)
    .map((from, index) =>
      limiter.execute(() => routeSegment(from, routePoints[index + 1], profile)),
    )
  const segments = await Promise.all(segmentPromises)

  if (segments.some((segment) => segment === null)) return null

  return segments as Segment[]
}
